import { useRef, ReactNode } from 'react';
import { DocumentProvider, useDocument } from '../documents/DocumentContext';
import {
    saveNordInsightTemplateDocument,
    completeNordInsightTemplateDocument,
    createNordInsightTemplate,
    PopulatedPolicy,
} from '../../services/api/policyService';
import { DocumentStatus } from '../documents/types';
import {
    stripConditionalsFromDocumentData,
    toPersistedPolicyConditionals,
} from '../department-policies/conditionals/conditionalsUtils';

interface TemplateProviderProps {
    children: ReactNode;
    policyId?: string;
    documentData?: { data?: Record<string, any>; status?: DocumentStatus };
    getTitle: () => string;
    schemaKey?: string | null;
    onCreated?: (policy: PopulatedPolicy) => void;
    onCompleted?: () => void;
}

export function TemplateProvider({
    children,
    policyId: initialPolicyId,
    documentData,
    getTitle,
    schemaKey,
    onCreated,
    onCompleted,
}: TemplateProviderProps) {
    const policyIdRef = useRef(initialPolicyId);
    const createdRef = useRef(false);

    const ensurePolicy = async (): Promise<PopulatedPolicy> => {
        if (!schemaKey) throw new Error('No schema specified');
        const newPolicy = await createNordInsightTemplate({ schemaKey });
        createdRef.current = true;
        policyIdRef.current = newPolicy._id;
        onCreated?.(newPolicy);
        return newPolicy;
    };

    return (
        <DocumentProvider
            documentId={policyIdRef.current}
            documentData={documentData}
            saveDocument={async (data) => {
                const title = getTitle();
                const persistedConditionals = toPersistedPolicyConditionals(
                    data.policyConditionals
                );
                const documentPayload = stripConditionalsFromDocumentData(data);

                if (!policyIdRef.current) {
                    if (createdRef.current) return {};
                    const newPolicy = await ensurePolicy();
                    const result = await saveNordInsightTemplateDocument(
                        newPolicy._id,
                        documentPayload,
                        title || undefined,
                        undefined,
                        { conditionals: persistedConditionals }
                    );
                    return { documentId: result._id, status: result.status };
                }

                const result = await saveNordInsightTemplateDocument(
                    policyIdRef.current,
                    documentPayload,
                    title || undefined,
                    undefined,
                    { conditionals: persistedConditionals }
                );
                return { documentId: result._id, status: result.status };
            }}
            completeDocument={async (data) => {
                const title = getTitle();
                const persistedConditionals = toPersistedPolicyConditionals(
                    data.policyConditionals
                );
                const documentPayload = stripConditionalsFromDocumentData(data);

                if (!policyIdRef.current) {
                    if (createdRef.current) return {};
                    const newPolicy = await ensurePolicy();
                    const result = await completeNordInsightTemplateDocument(
                        newPolicy._id,
                        documentPayload,
                        title || undefined,
                        undefined,
                        { conditionals: persistedConditionals }
                    );
                    onCompleted?.();
                    return { documentId: result._id, status: result.status };
                }

                const result = await completeNordInsightTemplateDocument(
                    policyIdRef.current,
                    documentPayload,
                    title || undefined,
                    undefined,
                    { conditionals: persistedConditionals }
                );
                onCompleted?.();
                return { documentId: result._id, status: result.status };
            }}
        >
            {children}
        </DocumentProvider>
    );
}

export const useTemplate = () => {
    const context = useDocument();
    return {
        data: context.data,
        updateData: context.updateData,
        saveTemplate: context.save,
        completeTemplate: context.complete,
        isSaving: context.isSaving,
        isLoading: context.isLoading,
        templateStatus: context.documentStatus,
        setValue: context.setValue,
    };
};
